import { Pipe, PipeTransform } from '@angular/core';
import { ItemConfig, ItemProfile, ItemProperties, ItemResource, PropertyProfile } from '@ngx-hal/models';
import { PropertyResourceMetaPipe } from '../property-resource-meta/property-resource-meta.pipe';
import { PropertiesProfilesPipeOptions } from './properties-profiles.pipe.options';

@Pipe({
  name: 'propertiesProfiles',
  standalone: false,
})
export class PropertiesProfilesPipe implements PipeTransform {
  private static readonly TechnicalsProperties: string[] = [
    'createdBy',
    'createdDate',
    'lastModifiedBy',
    'lastModifiedDate',
  ];
  private itemProfile?: ItemProfile<never>;
  private propertiesProfiles?: PropertyProfile<never, ItemProperties<never>>[];

  /**
   * Transforms an `ItemProfile` into an array of `PropertyProfile` objects, applying various filtering and transformation rules
   * based on the provided `itemConfig`, `options`, and `isTab` flag.
   *
   * @template Entity - The type of the item resource.
   * @param itemProfile - The profile of the item containing ALPS descriptors and schema definitions.
   * @param itemConfig - (Optional) Configuration object for the item, used to filter and sort properties.
   * @param options - (Optional) Options to control which properties are included or removed, such as parents, children, technicals, and functionals.
   * @param isTab - (Optional) If true, applies a simplified filtering logic suitable for tabbed views.
   * @returns An array of `PropertyProfile` objects representing the filtered and transformed properties of the item.
   */
  transform<Entity extends ItemResource>(
    itemProfile: ItemProfile<Entity>,
    itemConfig?: ItemConfig<Entity>,
    options?: PropertiesProfilesPipeOptions<Entity>,
    isTab?: boolean,
  ): PropertyProfile<Entity, ItemProperties<Entity>>[] {
    if (this.itemProfile?.alps?.href !== itemProfile?.alps?.href) {
      this.itemProfile = itemProfile;
      if (isTab) {
        this.propertiesProfiles = this.itemProfile.alps.descriptor
          ?.map(propertyAlps => ({
            alps: propertyAlps,
            schema: this.itemProfile?.schema.properties[propertyAlps.name],
          }))

          // set immutable property
          .map(propertyProfile => {
            if (propertyProfile.schema && propertyProfile.alps.rt?.match(/immutable/)) {
              propertyProfile.schema.immutable = true;
            }

            return propertyProfile;
          })
          .filter(propertyProfile => {
            if (itemConfig && isTab) {
              return itemConfig?.[propertyProfile.alps.name as keyof typeof itemConfig];
            }
            return propertyProfile;
          });
      } else {
        this.propertiesProfiles = this.itemProfile.alps.descriptor
          ?.map(propertyAlps => ({
            alps: propertyAlps,
            schema: this.itemProfile?.schema.properties[propertyAlps.name],
          }))

          // set immutable property
          .map(propertyProfile => {
            if (propertyProfile.schema && propertyProfile.alps.rt?.match(/immutable/)) {
              propertyProfile.schema.immutable = true;
            }

            return propertyProfile;
          })

          // remove parents properties
          .filter(
            propertyProfile => !(options?.remove?.parents === true && propertyProfile.alps.rt?.match(/type=parent/)),
          )

          // remove specific parents properties
          .filter(
            propertyProfile =>
              !Array.isArray(options?.remove?.parents) ||
              !(
                propertyProfile.alps.rt &&
                options?.remove?.parents.find(
                  parent =>
                    parent.profile.href === new PropertyResourceMetaPipe().transform(propertyProfile)?.profile.href,
                )
              ),
          )

          // remove sibling properties
          .filter(
            propertyProfile => !(options?.remove?.parents === true && propertyProfile.alps.rt?.match(/type=sibling/)),
          )

          // remove children properties
          .filter(
            propertyProfile => !(options?.remove?.children === true && propertyProfile.alps.rt?.match(/type=child/)),
          )

          // remove specific children properties
          .filter(
            propertyProfile =>
              !Array.isArray(options?.remove?.children) ||
              !(
                propertyProfile.alps.rt && options?.remove?.children?.find(child => child === propertyProfile.alps.name)
              ),
          )

          // remove technicals properties
          .filter(
            propertyProfile =>
              !(
                options?.remove?.technicals === true &&
                PropertiesProfilesPipe.TechnicalsProperties.includes(propertyProfile.alps.name)
              ),
          )

          // remove functionals properties
          .filter(
            propertyProfile =>
              !(
                options?.remove?.functionals === true &&
                !propertyProfile.alps.rt &&
                !PropertiesProfilesPipe.TechnicalsProperties.includes(propertyProfile.alps.name)
              ),
          )

          // remove specific functionals properties
          .filter(
            propertyProfile =>
              !Array.isArray(options?.remove?.functionals) ||
              !(
                !propertyProfile.alps.rt &&
                options?.remove?.functionals.find(functional => functional.alps.name === propertyProfile.alps.name)
              ),
          );
      }
    }
    return (
      (this.propertiesProfiles as PropertyProfile<Entity, ItemProperties<Entity>>[])
        // remove hidden properties
        ?.filter(
          propertyProfile =>
            options?.keep?.hidden === true || itemConfig?.[propertyProfile.alps.name]?._hidden !== true,
        )

        // sort properties
        .sort(
          (propertyProfileA, propertyProfileB) =>
            (itemConfig?.[propertyProfileA.alps.name]?._order ?? 0) -
            (itemConfig?.[propertyProfileB.alps.name]?._order ?? 0),
        )
    );
  }
}
